import React, { useState, useEffect, useCallback } from "react";
import {
  Container,
  Typography,
  TextField,
  Button,
  Paper,
  Stack,
  Box,
  Snackbar,
  Alert,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  MenuItem,
  Chip,
  Divider,
  Zoom,
  Fade,
} from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";
import { Edit as EditIcon, Delete as DeleteIcon, Close as CloseIcon } from "@mui/icons-material";
import axios from "axios";
import { useNavigate } from "react-router-dom";

const TICKET_URL = `${process.env.REACT_APP_API_URL}/api/tele`;

const lightPaper = {
  backgroundColor: "#ffffff",
  border: "1px solid #e0e0e0",
  borderRadius: "12px",
  color: "#000000",
  boxShadow: "0 4px 20px rgba(0, 0, 0, 0.05)"
};

const TicketsOnly = () => {
  const navigate = useNavigate();
  const token = localStorage.getItem("token");

  // States
  const [tickets, setTickets] = useState([]);
  const [editingTicket, setEditingTicket] = useState(null);
  const [editData, setEditData] = useState({
    CCTNumber: "",
    status: "",
    description: "",
    primaryContact: "",
    secondaryContact: "",
  });
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [ticketSearch, setTicketSearch] = useState("");
  const [ticketLoading, setTicketLoading] = useState(false);
  const [ticketStatusFilter, setTicketStatusFilter] = useState("Open");
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  const showSnackbar = (msg, severity = "success") =>
    setSnackbar({ open: true, message: msg, severity });
  const handleCloseSnackbar = () => setSnackbar({ ...snackbar, open: false });

  // ===== Fetch Tickets =====
  const fetchTickets = useCallback(async () => {
    try {
      setTicketLoading(true);
      const res = await axios.get(`${TICKET_URL}/tickets/all`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setTickets(Array.isArray(res.data?.tickets) ? res.data.tickets : []);
    } catch (err) {
      showSnackbar(err.response?.data?.msg || "Failed to fetch tickets", "error");
      setTickets([]);
    } finally {
      setTicketLoading(false);
    }
  }, [token]);

  // Load data on mount
  useEffect(() => {
    fetchTickets();
  }, [fetchTickets]);

  // ===== Ticket Handlers =====
  const handleEditChange = (e) =>
    setEditData({ ...editData, [e.target.name]: e.target.value });

  const updateTicket = async () => {
    try {
      const updateData = {
        CCTNumber: editData.CCTNumber,
        status: editData.status,
        description: editData.description,
        primaryContact: editData.primaryContact,
        secondaryContact: editData.secondaryContact,
      };

      await axios.put(`${TICKET_URL}/ticket/${editingTicket._id}`, updateData, {
        headers: { Authorization: `Bearer ${token}` },
      });
      showSnackbar("Ticket updated successfully!");
      fetchTickets();
      setShowEditDialog(false);
      setEditingTicket(null);
      setEditData({
        CCTNumber: "",
        status: "",
        description: "",
        primaryContact: "",
        secondaryContact: "",
      });
    } catch (err) {
      showSnackbar(err.response?.data?.msg || "Failed to update ticket", "error");
    }
  };

  const deleteTicket = async (ticketId) => {
    if (!window.confirm("Are you sure you want to delete this ticket?")) return;

    try {
      await axios.delete(`${TICKET_URL}/ticket/${ticketId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      showSnackbar("Ticket deleted successfully!");
      fetchTickets();
    } catch (err) {
      showSnackbar(err.response?.data?.msg || "Failed to delete ticket", "error");
    }
  };

  const closeTicket = async (ticketId) => {
    try {
      await axios.patch(`${TICKET_URL}/ticket/${ticketId}/close`, {}, {
        headers: { Authorization: `Bearer ${token}` },
      });
      showSnackbar("Ticket closed successfully!");
      fetchTickets();
    } catch (err) {
      showSnackbar("Failed to close ticket", "error");
    }
  };

  // ===== Filter Tickets =====
  const filteredTickets = tickets.filter((t) => {
    const searchMatch = ticketSearch === "" ||
      t.serviceNumber?.toLowerCase().includes(ticketSearch.toLowerCase()) ||
      t.CCTNumber?.toLowerCase().includes(ticketSearch.toLowerCase()) ||
      t.description?.toLowerCase().includes(ticketSearch.toLowerCase()) ||
      t.name?.toLowerCase().includes(ticketSearch.toLowerCase()) ||
      t.company?.toLowerCase().includes(ticketSearch.toLowerCase());

    const statusMatch = ticketStatusFilter === "All" ||
      t.ticketStatus?.toLowerCase() === ticketStatusFilter.toLowerCase();

    return searchMatch && statusMatch;
  });

  // Status color mapping
  const getStatusColor = (status) => {
    const colors = {
      Active: "success",
      Down: "error",
      Intermittent: "warning",
      Pending: "info",
      Ongoing: "primary",
      Closed: "default"
    };
    return colors[status] || "default";
  };

  // Handle edit click - only allow editing for open tickets
  const handleEditClick = (ticket) => {
    if (ticket.ticketStatus !== "Open") {
      showSnackbar("Only open tickets can be edited", "warning");
      return;
    }
    setEditingTicket(ticket);
    setEditData({
      CCTNumber: ticket.CCTNumber || "",
      status: ticket.status || "",
      description: ticket.description || "",
      primaryContact: ticket.primaryContact || "",
      secondaryContact: ticket.secondaryContact || "",
    });
    setShowEditDialog(true);
  };

  return (
    <Box sx={{ minHeight: "100vh", bgcolor: "#ffffff", py: 2 }}>
      <Container
        maxWidth={false}
        sx={{
          width: { xs: "100%", md: "calc(100% - 96px)" },
          ml: { xs: 0, md: "84px" },
          mr: { xs: 0, md: "12px" },
          px: { xs: 1, md: 0.5 },
        }}
      >
        {/* --- FILTER BAR --- */}
        <Paper
          sx={{
            p: 1.5,
            mb: 2,
            ...lightPaper,
            display: "flex",
            flexWrap: "wrap",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 2
          }}
        >
          <Box display="flex" alignItems="center" gap={2} flexWrap="wrap">
            <TextField
              label="Search tickets..."
              size="small"
              value={ticketSearch}
              onChange={(e) => setTicketSearch(e.target.value)}
              sx={{ flex: 1, minWidth: 250 }}
              placeholder="Search by name, company, service no, CCT..."
            />
            <IconButton onClick={fetchTickets} disabled={ticketLoading} sx={{ bgcolor: "#f0f0f0", '&:hover': { bgcolor: "#e0e0e0" } }}>
              <RefreshIcon />
            </IconButton>
            <TextField
              select
              label="Ticket Status"
              size="small"
              value={ticketStatusFilter}
              onChange={(e) => setTicketStatusFilter(e.target.value)}
              sx={{ minWidth: 150 }}
            >
              <MenuItem value="Open">Open</MenuItem>
              <MenuItem value="Closed">Closed</MenuItem>
              <MenuItem value="All">All</MenuItem>
            </TextField>

          </Box>

          {/* Filtered Count Display */}
          <Box display="flex" alignItems="center" gap={1}>
            <Chip
              label={`${filteredTickets.length} ticket${filteredTickets.length !== 1 ? 's' : ''} found`}
              size="small"
              sx={{
                bgcolor: "#f5c542",
                color: "black",
                fontWeight: 600,
                borderRadius: "8px",
                '& .MuiChip-label': {
                  px: 2,
                }
              }}
            />
            {ticketStatusFilter !== "All" && (
              <Typography variant="caption" sx={{ color: "#666666" }}>
                (out of {tickets.length} total)
              </Typography>
            )}
          </Box>
        </Paper>

        {/* Edit Ticket Dialog - Yellow and Black Theme */}
        <Dialog
          open={showEditDialog}
          onClose={() => setShowEditDialog(false)}
          maxWidth="sm"
          fullWidth
          TransitionComponent={Zoom}
          PaperProps={{
            sx: {
              borderRadius: "20px",
              background: "#ffffff",
              boxShadow: "0 20px 40px rgba(0,0,0,0.1)",
              overflow: "hidden",
              border: "1px solid #f5c542",
            }
          }}
        >
          <Box sx={{
            background: "#f5c542",
            p: 2,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}>
            <DialogTitle sx={{ p: 0, color: "black", fontWeight: 600 }}>
              Edit Ticket
            </DialogTitle>
            <IconButton
              onClick={() => setShowEditDialog(false)}
              sx={{ color: "black", '&:hover': { bgcolor: "rgba(0,0,0,0.1)" } }}
              size="small"
            >
              <CloseIcon />
            </IconButton>
          </Box>

          <DialogContent sx={{ p: 3, backgroundColor: "#ffffff", color: "#333333" }}>
            {/* Read-only info section */}
            <Paper elevation={0} sx={{ p: 2, mb: 3, bgcolor: "#fafafa", borderRadius: "12px", border: "1px solid #e0e0e0" }}>
              <Typography variant="caption" sx={{ display: "block", mb: 1, color: "#f5c542", fontWeight: 600 }}>
                Ticket Information (Read Only)
              </Typography>
              <Stack spacing={1.5}>
                <Box display="flex" justifyContent="space-between" alignItems="center">
                  <Typography variant="body2" sx={{ color: "#666666", fontWeight: 500 }}>Name:</Typography>
                  <Typography variant="body2" sx={{ fontWeight: 600, color: "#333333" }}>{editingTicket?.name || "N/A"}</Typography>
                </Box>
                <Divider sx={{ bgcolor: "#e0e0e0" }} />
                <Box display="flex" justifyContent="space-between" alignItems="center">
                  <Typography variant="body2" sx={{ color: "#666666", fontWeight: 500 }}>Company:</Typography>
                  <Typography variant="body2" sx={{ fontWeight: 600, color: "#333333" }}>{editingTicket?.company || "N/A"}</Typography>
                </Box>
                <Divider sx={{ bgcolor: "#e0e0e0" }} />
                <Box display="flex" justifyContent="space-between" alignItems="center">
                  <Typography variant="body2" sx={{ color: "#666666", fontWeight: 500 }}>Service Number:</Typography>
                  <Typography variant="body2" sx={{ fontWeight: 600, color: "#333333" }}>{editingTicket?.serviceNumber || "N/A"}</Typography>
                </Box>
              </Stack>
            </Paper>

            {/* Editable fields */}
            <Typography variant="caption" sx={{ display: "block", mb: 2, color: "#f5c542", fontWeight: 600 }}>
              Editable Fields
            </Typography>
            <Stack spacing={2.5}>
              <TextField
                label="CCT Number"
                name="CCTNumber"
                size="small"
                value={editData.CCTNumber}
                onChange={handleEditChange}
                fullWidth
                sx={{
                  input: { color: "#333333" },
                  label: { color: "#666666" },
                  '& .MuiOutlinedInput-root': {
                    borderRadius: "12px",
                    '& fieldset': { borderColor: "#e0e0e0" },
                    '&:hover fieldset': { borderColor: "#f5c542" },
                    '&.Mui-focused fieldset': { borderColor: "#f5c542" }
                  }
                }}
              />

              <TextField
                select
                label="Status"
                name="status"
                size="small"
                value={editData.status}
                onChange={handleEditChange}
                fullWidth
                sx={{
                  label: { color: "#666666" },
                  '& .MuiOutlinedInput-root': {
                    borderRadius: "12px",
                    '& fieldset': { borderColor: "#e0e0e0" },
                    '&:hover fieldset': { borderColor: "#f5c542" },
                    '&.Mui-focused fieldset': { borderColor: "#f5c542" }
                  },
                  '& .MuiSelect-icon': { color: "#666666" },
                  '& .MuiSelect-select': { color: "#333333" }
                }}
              >
                <MenuItem value="Pending">Pending</MenuItem>
                <MenuItem value="Ongoing">Ongoing</MenuItem>
                <MenuItem value="Active">Active</MenuItem>
                <MenuItem value="Down">Down</MenuItem>
                <MenuItem value="Intermittent">Intermittent</MenuItem>
                <MenuItem value="Closed">Closed</MenuItem>
              </TextField>

              <TextField
                label="Primary Contact"
                name="primaryContact"
                size="small"
                value={editData.primaryContact}
                onChange={handleEditChange}
                fullWidth
                sx={{
                  input: { color: "#333333" },
                  label: { color: "#666666" },
                  '& .MuiOutlinedInput-root': {
                    borderRadius: "12px",
                    '& fieldset': { borderColor: "#e0e0e0" },
                    '&:hover fieldset': { borderColor: "#f5c542" },
                    '&.Mui-focused fieldset': { borderColor: "#f5c542" }
                  }
                }}
              />

              <TextField
                label="Secondary Contact"
                name="secondaryContact"
                size="small"
                value={editData.secondaryContact}
                onChange={handleEditChange}
                fullWidth
                sx={{
                  input: { color: "#333333" },
                  label: { color: "#666666" },
                  '& .MuiOutlinedInput-root': {
                    borderRadius: "12px",
                    '& fieldset': { borderColor: "#e0e0e0" },
                    '&:hover fieldset': { borderColor: "#f5c542" },
                    '&.Mui-focused fieldset': { borderColor: "#f5c542" }
                  }
                }}
              />

              <TextField
                label="Description"
                name="description"
                size="small"
                value={editData.description}
                onChange={handleEditChange}
                fullWidth
                multiline
                rows={3}
                sx={{
                  input: { color: "#333333" },
                  textarea: { color: "#333333" },
                  label: { color: "#666666" },
                  '& .MuiOutlinedInput-root': {
                    borderRadius: "12px",
                    '& fieldset': { borderColor: "#e0e0e0" },
                    '&:hover fieldset': { borderColor: "#f5c542" },
                    '&.Mui-focused fieldset': { borderColor: "#f5c542" }
                  }
                }}
              />
            </Stack>
          </DialogContent>

          <DialogActions sx={{ p: 3, pt: 0, gap: 1, backgroundColor: "#ffffff" }}>
            <Button
              onClick={() => setShowEditDialog(false)}
              variant="outlined"
              sx={{
                borderRadius: "10px",
                textTransform: "none",
                px: 3,
                borderColor: "#f5c542",
                color: "#f5c542",
                '&:hover': { borderColor: "#e0b03b", color: "#e0b03b", bgcolor: "rgba(245, 197, 66, 0.05)" }
              }}
            >
              Cancel
            </Button>
            <Button
              onClick={updateTicket}
              variant="contained"
              sx={{
                borderRadius: "10px",
                textTransform: "none",
                px: 3,
                bgcolor: "#f5c542",
                color: "black",
                fontWeight: 600,
                '&:hover': {
                  bgcolor: "#e0b03b",
                }
              }}
            >
              Update Ticket
            </Button>
          </DialogActions>
        </Dialog>

        {/* Tickets Table - No Wrap, Horizontal Scroll */}
        <TableContainer
          component={Paper}
          sx={{
            borderRadius: "12px",
            overflowX: "auto",
            overflowY: "auto",
            maxHeight: { xs: "70vh", md: "none" },
            '&::-webkit-scrollbar': {
              height: 8,
              width: 8,
            },
            '&::-webkit-scrollbar-track': {
              backgroundColor: '#f1f1f1',
              borderRadius: 4,
            },
            '&::-webkit-scrollbar-thumb': {
              backgroundColor: '#888',
              borderRadius: 4,
              '&:hover': {
                backgroundColor: '#555',
              },
            },
          }}
        >
          <Table
            size="small"
            stickyHeader
            sx={{
              minWidth: 1400,
              tableLayout: 'auto',
              '& .MuiTableCell-root': {
                whiteSpace: 'nowrap',
                px: 2,
                py: 1.5,
              },
            }}
          >
            <TableHead>
              <TableRow sx={{ bgcolor: "#f8f9fa" }}>
                <TableCell sx={{ fontWeight: 600 }}>Name</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Company</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Service No</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>P. Contact</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>S. Contact</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Ticket Status</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Description</TableCell>
                <TableCell align="center" sx={{ fontWeight: 600 }}>Edit</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>CCT Number</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Created At</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Closed At</TableCell>

              </TableRow>
            </TableHead>
            <TableBody>
              {ticketLoading ? (
                <TableRow>
                  <TableCell colSpan={12} align="center" sx={{ py: 4 }}>
                    <CircularProgress />
                  </TableCell>
                </TableRow>
              ) : filteredTickets.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={12} align="center" sx={{ py: 4 }}>
                    <Typography color="textSecondary">No tickets found</Typography>
                  </TableCell>
                </TableRow>
              ) : (
                filteredTickets.map((t, index) => (
                  <TableRow key={t._id} sx={{ '&:hover': { bgcolor: "#fafafa" } }}>
                    <TableCell>
                      <Typography variant="body2" noWrap title={t.name}>
                        {t.name || "N/A"}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" noWrap title={t.company}>
                        {t.company || "N/A"}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" sx={{ fontWeight: 600 }} noWrap>
                        {t.serviceNumber || "N/A"}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" noWrap title={t.primaryContact}>
                        {t.primaryContact || "N/A"}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" noWrap title={t.secondaryContact}>
                        {t.secondaryContact || "N/A"}
                      </Typography>
                    </TableCell>
                    
                    <TableCell>
                      <Chip
                        label={t.status || "N/A"}
                        size="small"
                        color={getStatusColor(t.status)}
                        sx={{ borderRadius: "8px", fontWeight: 500 }}
                      />
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={t.ticketStatus || "N/A"}
                        size="small"
                        color={t.ticketStatus === "Open" ? "warning" : "success"}
                        sx={{ borderRadius: "8px", fontWeight: 500 }}
                      />
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" noWrap title={t.description}>
                        {t.description || "N/A"}
                      </Typography>
                    </TableCell>
                    <TableCell align="center">
                      <Stack direction="row" spacing={0.5} justifyContent="center">
                        <IconButton
                          size="small"
                          onClick={() => handleEditClick(t)}
                          disabled={t.ticketStatus !== "Open"}
                          title={t.ticketStatus !== "Open" ? "Only open tickets can be edited" : "Edit ticket"}
                          sx={{
                            color: "#f5c542",
                            '&:hover': { bgcolor: "rgba(245, 197, 66, 0.1)" }
                          }}
                        >
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </Stack>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" noWrap title={t.CCTNumber}>
                        {t.CCTNumber || "N/A"}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="caption" noWrap title={t.createdAt ? new Date(t.createdAt).toLocaleString() : "N/A"}>
                        {t.createdAt ? new Date(t.createdAt).toLocaleString() : "N/A"}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="caption" noWrap title={t.closedAt ? new Date(t.closedAt).toLocaleString() : "N/A"}>
                        {t.closedAt ? new Date(t.closedAt).toLocaleString() : "N/A"}
                      </Typography>
                    </TableCell>
                    
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>

        {/* Snackbar */}
        <Snackbar
          open={snackbar.open}
          autoHideDuration={3000}
          onClose={handleCloseSnackbar}
          anchorOrigin={{ vertical: "top", horizontal: "center" }}
          TransitionComponent={Fade}
        >
          <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} sx={{ width: "100%", borderRadius: "10px" }}>
            {snackbar.message}
          </Alert>
        </Snackbar>
      </Container>
    </Box>
  );
};

export default TicketsOnly;